import React, { useState, useMemo, useCallback } from 'react';
import { Card, Table, Select, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { TopMerchantItem } from '../../types/dashboardFounder';
import type { ColumnsType } from 'antd/es/table';
import { formatVnd } from '../../utils/formatVnd';
import { ChartSkeleton } from '../dashboard/ChartSkeleton';
import { DashboardEmptyState } from '../dashboard/DashboardEmptyState';

type SortKey = 'gmv' | 'orders' | 'cancelRate' | 'avgPrepTimeMin';

interface TopMerchantsTableProps {
  data: TopMerchantItem[];
  loading?: boolean;
}

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'gmv', label: 'GMV' },
  { value: 'orders', label: 'Đơn hàng' },
  { value: 'cancelRate', label: 'Tỷ lệ hủy' },
  { value: 'avgPrepTimeMin', label: 'Thời gian chuẩn bị' },
];

export const TopMerchantsTable: React.FC<TopMerchantsTableProps> = ({
  data,
  loading = false,
}) => {
  const [sortBy, setSortBy] = useState<SortKey>('gmv');

  const sortedData = useMemo(() => {
    const arr = [...data];
    arr.sort((a, b) => {
      const aVal = a[sortBy] ?? 0;
      const bVal = b[sortBy] ?? 0;
      if (sortBy === 'cancelRate' || sortBy === 'avgPrepTimeMin') {
        return bVal - aVal;
      }
      return (bVal as number) - (aVal as number);
    });
    return arr;
  }, [data, sortBy]);

  const handleDownloadCsv = useCallback(() => {
    const headers = [
      'Merchant',
      'Orders',
      'GMV',
      'AOV',
      'Cancel Rate (%)',
      'Avg Prep Time (min)',
      'Rating',
    ];
    const rows = sortedData.map(d => [
      d.name,
      d.orders,
      d.gmv,
      d.aov,
      (d.cancelRate ?? 0).toFixed(1),
      (d.avgPrepTimeMin ?? 0).toFixed(1),
      d.rating != null ? d.rating.toFixed(1) : '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `top-merchants-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [sortedData]);

  const columns: ColumnsType<TopMerchantItem> = useMemo(
    () => [
      {
        title: 'Merchant',
        dataIndex: 'name',
        key: 'name',
        render: (name: string, record) => (
          <Link to={`/merchants/${record.merchantId}`}>{name}</Link>
        ),
        sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      },
      {
        title: 'Đơn hàng',
        dataIndex: 'orders',
        key: 'orders',
        align: 'right',
        render: (v: number) => v?.toLocaleString(),
      },
      {
        title: 'GMV',
        dataIndex: 'gmv',
        key: 'gmv',
        align: 'right',
        render: (v: number) => formatVnd(v ?? 0),
      },
      {
        title: 'AOV',
        dataIndex: 'aov',
        key: 'aov',
        align: 'right',
        render: (v: number) => formatVnd(v ?? 0),
      },
      {
        title: 'Tỷ lệ hủy',
        dataIndex: 'cancelRate',
        key: 'cancelRate',
        align: 'right',
        render: (v: number) => `${(v ?? 0).toFixed(1)}%`,
      },
      {
        title: 'TB chuẩn bị (phút)',
        dataIndex: 'avgPrepTimeMin',
        key: 'avgPrepTimeMin',
        align: 'right',
        render: (v: number) => (v ?? 0).toFixed(1),
      },
      {
        title: 'Rating',
        dataIndex: 'rating',
        key: 'rating',
        align: 'right',
        render: (v?: number) => (v != null ? v.toFixed(1) : '–'),
      },
    ],
    []
  );

  if (loading) {
    return (
      <Card className="founder-chart-card" style={{ borderRadius: 12 }}>
        <h3 className="founder-chart-title">Top Merchants</h3>
        <ChartSkeleton height={320} />
      </Card>
    );
  }

  if (!data.length) {
    return (
      <Card className="founder-chart-card" style={{ borderRadius: 12 }}>
        <h3 className="founder-chart-title">Top Merchants</h3>
        <DashboardEmptyState
          message="Chưa có dữ liệu"
          suggestion="Chọn khoảng ngày khác."
        />
      </Card>
    );
  }

  return (
    <Card className="founder-chart-card" style={{ borderRadius: 12 }}>
      <div className="founder-chart-header founder-chart-header-row">
        <h3 className="founder-chart-title">Top Merchants</h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Select
            size="small"
            value={sortBy}
            onChange={v => setSortBy(v)}
            options={SORT_OPTIONS}
            style={{ width: 160 }}
          />
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={handleDownloadCsv}
          >
            Download CSV
          </Button>
        </div>
      </div>
      <Table
        dataSource={sortedData}
        columns={columns}
        rowKey="merchantId"
        pagination={false}
        size="small"
        scroll={{ x: 600 }}
      />
    </Card>
  );
};
